<template>
  <div class="layout-column">

    <!-- Warning if cancer register is not found -->

    <div class="flex-gt-sm-100 flex-100 mx-2 mb-4" v-if="!userCancerRegister && !isDisabled">
      <div style="border:1px solid;border-radius: 9px;"
           class="pa-3"
           :class="[!compColor.isInDarkMode ? 'red' + ' lighten-5 white--text ' : '']"
           :style="{'border-color': compColor.isInDarkMode ? '#E53935' : 'transparent'}"
      >
        <v-icon class="mr-3" size="30" :color="'#E53935'">mdi-alert</v-icon>
        <span v-if="userIsPhysician" style="font-size: 18px;" :class="'red--text darken-1'"><small>{{ $t('WARNING_MISSING_CANCER_REGISTER') }}</small></span>
        <span v-if="!userIsPhysician" style="font-size: 18px;" :class="'red--text darken-1'"><small>{{ $t('WARNING_MISSING_CANCER_REGISTER_EMPLOYEE') }}</small></span>

        <v-btn :color="'#E53935'"
               v-if="userIsPhysician"
               dark
               depressed
               class="text-none ml-4"
               style="height: 28px;"
               @click="openAccountTab()"
        >{{ $t('WARNING_MISSING_CANCER_REGISTER_BTN_TEXT') }}</v-btn>
      </div>
    </div>

    <div class="flex-gt-sm-100 flex-100 layout-row layout-wrap">

      <!-- Primärtumor Diagnosedatum -->

      <div class="flex-gt-sm-25 flex-100 px-2 mb-4">
        <validation-provider tag="div"
                             class="layout-column"
                             :name="path + '.diagnosedatum'"
                             :ref="path + '.diagnosedatum'"
                             :rules="{required:true,german_date:true,date_not_in_future:true,diag_date_must_be_oldest_date:[formId],date_must_be_lesser_than_death:[deathDate,model.diagnosedatum_genauigkeit]}"
                             v-slot="{ errors }">
          <lx-datepicker
            :color="compColor.textInput"
            :name="path + '.diagnosedatum'"
            :label="$t('FORMS_DIVERSE.ICD_CODE_DATE') + ' *'"
            :disabled="isDisabled"
            :auto-focus="!formId"
            @input="setDateAccuracyAfterDate"
            :dense="denseFields"
            hide-details
            v-model="model.diagnosedatum"
          ></lx-datepicker>

          <!-- Render error separately to be able to see it, when field is disabled -->
          <div v-if="errors[0]" class="layout-row ml-2" style="margin-top: 1px;">
            <span class="error--text" style="font-size: 12px;">{{ errors[0] }}</span>
          </div>
        </validation-provider>
      </div>

      <!-- Primärtumor Datumsgenauigkeit Diagnosedatum -->

      <div class="flex-gt-sm-25 flex-100 px-2 layout-row">
        <validation-provider tag="div"
                             class="flex"
                             :name="path + '.diagnosedatum_genauigkeit'"
                             :ref="path + '.diagnosedatum_genauigkeit'"
                             rules="required"
                             v-slot="{ errors }">
          <v-select
            :color="compColor.primeColor"
            :label="$t('FORMS_DIVERSE.ICD_CODE_DATE_ACCURACY') + ' *'"
            v-model="model.diagnosedatum_genauigkeit"
            :items="DateAccuracyTypes"
            :disabled="isDisabled"
            outlined
            @change="validateDateAfterChangeOfAccuracy()"
            :hide-details="!errors || errors.length <= 0"
            :dense="denseFields"
            :error-messages="errors[0]"
            :menu-props="{ bottom: true, offsetY: true }"
            :dark="compColor.isInDarkMode">
            <template v-slot:item="{ item, on, attrs }">
              <v-list-item v-on="on"
                           :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                <v-list-item-content class="py-0">
                  <label style="cursor:pointer;"
                         :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                         class="lighten-4 text--darken-2">{{ item.text }}</label>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-select>
        </validation-provider>

        <v-tooltip bottom open-delay="1000">
          <template v-slot:activator="{ on }">
            <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-start pt-2 ml-3"
                 :class="{'mt-1': !denseFields}"
                 style="cursor: pointer;">
              <v-icon :color="compColor.prime" medium>info</v-icon>
            </div>
          </template>
          <p style="max-width: 400px;" class="my-1">{{ $t('FORMS_DIVERSE.TOOLTIP_DATE_ACCURACY') }}</p>
        </v-tooltip>
      </div>

      <div class="flex-gt-sm-50 flex-100 px-2"></div>
    </div>

    <div class="flex-gt-sm-100 flex-100 px-2 mb-1"></div>

    <!-- Primärtumor ICD10-Code -->

    <div class="flex-gt-sm-25 flex-100 px-2 mb-3">
      <validation-provider tag="div"
                           :name="path + '.primaertumor_icd_code'"
                           :ref="path + '.primaertumor_icd_code'"
                           rules="required|icd10_code_auto_complete"
                           v-slot="{ errors }">
        <lx-autocomplete
          :label="$t('FORMS_DIVERSE.ICD_CODE') + ' *'"
          v-model="model.primaertumor_icd_code"
          :error="errors[0]"
          @search-method="getDataForAutocomplete"
          @input="setDiagnosis"
          :disabled="isDisabled || diagnosisCodeIsDisabled"
          :length="3"
          :setValueOnlyPerChoice="true"
          :dense="denseFields"
          :hint="$t('FORMS_DIVERSE.CANCER_DIAGNOSIS_HINT')"
          :persistent_hint="true"
          :results="resultSearchModel">
          <template v-slot="slotProps">
            <div class="layout-row">
              <div style="width: 100px;border-radius: 4px;"
                   class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                <h3>
                  <lx-text-highlight :text="slotProps.lineItem.code"
                                     :search-string="slotProps.searchText"
                                     :color="compColor.prime"></lx-text-highlight>
                </h3>
              </div>
              <div class="flex">
                <span class="body-2">
                  <lx-text-highlight :text="slotProps.lineItem.description" :search-string="slotProps.searchText"
                                     :disableDarkMode="true"
                                     :color="compColor.prime"></lx-text-highlight>
                </span>
              </div>
            </div>
          </template>
        </lx-autocomplete>
      </validation-provider>
    </div>

    <!-- Primärtumor ICD-Version -->

    <div class="flex-gt-sm-25 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.primaertumor_icd_version'"
                           :ref="path + '.primaertumor_icd_version'"
                           rules="required"
                           v-slot="{ errors }">
        <v-select
          :color="compColor.primeColor"
          :label="$t('FORMS_DIVERSE.ICD_CODE_VERSION') + ' *'"
          v-model="model.primaertumor_icd_version"
          :items="Primaertumor_ICD_VersionTypes"
          :disabled="isDisabled || ((classifications.length > 0 && !!model.primaertumor_icd_code) || Object.keys(multiFormsCount).length > 0) && $refs[path + '.primaertumor_icd_version'] && $refs[path + '.primaertumor_icd_version'].errors.length === 0"
          @change="resetICD10Code()"
          outlined
          :dense="denseFields"
          :error-messages="errors[0]"
          :persistent-hint="true"
          :hint="$t('FORMS_DIVERSE.CODE_VERSION_HINT')"
          :menu-props="{ bottom: true, offsetY: true }"
          :dark="compColor.isInDarkMode">
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-on="on"
                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
              <v-list-item-content class="py-0">
                <label style="cursor:pointer;"
                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                       class="lighten-4 text--darken-2">{{ item.text }}</label>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
      </validation-provider>
    </div>

    <!-- Primärtumor Diagnosetext -->

    <div class="flex-gt-sm-50 flex-100 px-2 mb-4">
      <validation-provider tag="div"
                           :name="path + '.primaertumor_diagnosetext'"
                           :ref="path + '.primaertumor_diagnosetext'"
                           rules="trim_string"
                           v-slot="{ errors }">
        <long-input
          :color="compColor.primeColor"
          :name="path + '.primaertumor_diagnosetext'"
          :label="$t('FORMS_DIVERSE.ICD_CODE_TEXT')"
          v-model="model.primaertumor_diagnosetext"
          :disabled="isDisabled"
          :dense="denseFields"
          :error="errors[0]"
        ></long-input>
      </validation-provider>
    </div>

    <!-- Warnung, wenn Folgeformulare und/oder Weitere Klassifikationen existieren-->

    <div class="layout-wrap flex-gt-sm-100 flex-100 mb-8 mx-2"
         v-if="Object.keys(multiFormsCount).length > 0 || classifications.length > 0">
      <lx-alert-box color="orange">
        <span v-if="Object.keys(multiFormsCount).length > 0 && classifications.length === 0">{{ $t('FORMS_DIVERSE.WARNING_ICD_CODE_EXISTING_FORMS') }}</span>
        <span v-if="Object.keys(multiFormsCount).length === 0 && classifications.length > 0">{{ $t('FORMS_DIVERSE.WARNING_ICD_CODE_EXISTING_CLASSIFICATIONS') }}</span>
        <span v-if="Object.keys(multiFormsCount).length > 0 && classifications.length > 0">{{ $t('FORMS_DIVERSE.WARNING_ICD_CODE_EXISTING_FORMS_AND_CLASSIFICATIONS') }}</span>
        <div v-if="Object.keys(multiFormsCount).length > 0 && Object.keys(multiFormsCount).indexOf('death') > -1"><span>{{ $t('FORMS_DIVERSE.WARNING_ICD_CODE_EXISTING_DEATH_FORM') }}</span></div>
      </lx-alert-box>
    </div>

    <div class="flex-gt-sm-100 flex-100 px-2" v-if="Object.keys(multiFormsCount).length ===  0 && classifications.length === 0"></div>

    <!-- Primärtumor ICD-O-Code -->

    <div class="flex-gt-sm-25 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.primaertumor_topographie_icd_o'"
                           :ref="path + '.primaertumor_topographie_icd_o'"
                           rules="required|icd_o_code_auto_complete"
                           v-slot="{ errors }">
        <lx-autocomplete
          :label="$t('FORMS_DIVERSE.ICD_O_CODE') + ' *'"
          v-model="model.primaertumor_topographie_icd_o"
          :error="errors[0]"
          @search-method="getICDOForAutocomplete"
          @input="setICDODiagnosis"
          :disabled="isDisabled"
          :dense="denseFields"
          :results="resultSearchICDOModel">
          <template v-slot="slotProps">
            <div class="layout-row">
              <div style="width: 100px;border-radius: 4px;"
                   class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                <h3>
                  <lx-text-highlight :text="slotProps.lineItem.code"
                                     :search-string="slotProps.searchText"
                                     :color="compColor.prime"></lx-text-highlight>
                </h3>
              </div>
              <div class="flex">
                    <span class="body-2">
                      <lx-text-highlight :text="slotProps.lineItem.description" :search-string="slotProps.searchText"
                                         :color="compColor.prime"></lx-text-highlight>
                    </span>
              </div>
            </div>
          </template>
        </lx-autocomplete>
      </validation-provider>
    </div>

    <!-- Primärtumor IC-O-Version -->

    <div class="flex-gt-sm-25 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.primaertumor_topographie_icd_o_version'"
                           :ref="path + '.primaertumor_topographie_icd_o_version'"
                           rules=""
                           v-slot="{ errors }">
        <v-select
          :color="compColor.primeColor"
          :label="$t('FORMS_DIVERSE.ICD_O_VERSION') + ' *'"
          v-model="model.primaertumor_topographie_icd_o_version"
          :items="Primaertumor_ICD_O_VersionTypes"
          :disabled="isDisabled"
          @change="resetICDOCode()"
          outlined
          :dense="denseFields"
          :persistent-hint="true"
          :hint="$t('FORMS_DIVERSE.CODE_VERSION_HINT')"
          :error-messages="errors[0]"
          :menu-props="{ bottom: true, offsetY: true }"
          :dark="compColor.isInDarkMode">
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-on="on"
                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
              <v-list-item-content class="py-0">
                <label style="cursor:pointer;"
                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                       class="lighten-4 text--darken-2">{{ item.text }}</label>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
      </validation-provider>
    </div>

    <!-- Primärtumor IC-O-Text -->

    <div class="flex-gt-sm-50 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.primaertumor_topographie_icd_freitext'"
                           :ref="path + '.primaertumor_topographie_icd_freitext'"
                           rules="trim_string"
                           v-slot="{ errors }">
        <long-input
          :color="compColor.primeColor"
          :name="path + '.primaertumor_topographie_icd_freitext'"
          :label="$t('FORMS_DIVERSE.ICD_O_CODE_TEXT')"
          v-model="model.primaertumor_topographie_icd_freitext"
          :disabled="isDisabled"
          :dense="denseFields"
          :error="errors[0]"
        ></long-input>
      </validation-provider>
    </div>

    <!-- switch report or ignore for report new diagnosis -->

    <div v-if="isNewDiagnosis" class="flex-gt-sm-100 flex-100 layout-row px-2 mb-2">
      <label :style="{color: compColor.primeColor}" class="pt-2 mr-3 ml-1">{{ $t('FORMS_DIVERSE.MODULE_DIAGNOSIS_SWITCH_NEW_DIAGNOSIS') }}</label>
      <v-btn-toggle v-model="setReportValue"
                    dense
                    class="ml-6"
                    :mandatory="setReportValue!== undefined && setReportValue!=null"
                    style="margin-top:2px; background-color: transparent;"
                    :color="compColor.prime">
        <v-btn :key="index" v-for="(kind, index) in listBtnGroupReportDiagnosis" :value="kind.value">
          {{ kind.text }}
        </v-btn>
      </v-btn-toggle>

      <v-tooltip bottom open-delay="1000">
        <template v-slot:activator="{ on }">
          <div v-on="on" class="blue--text text--lighten-1 layout-row layout-align-start-center px-2 ml-3"
               style="cursor: pointer;">
            <v-icon :color="compColor.prime" medium>info</v-icon>
          </div>
        </template>
        <p style="max-width: 480px;" class="ma-2">{{ $t('FORMS_DIVERSE.MODULE_DIAGNOSIS_TOOLTIP_NEW_DIAGNOSIS') }}</p>
      </v-tooltip>
    </div>

    <!-- Primärtumor Seitenlokalisation -->

    <div class="flex-100 px-2 mb-4">
      <validation-provider tag="div"
                           :name="path + '.seitenlokalisation'"
                           :ref="path + '.seitenlokalisation'"
                           class="flex layout-column"
                           :rules="{required: true}"
                           v-slot="{ errors }">
        <fake-input-frame
          :label="isPairedPrimaryTumor(model.primaertumor_icd_code) ? $t('FORMS_DIVERSE.ICD_CODE_SIDE_LOCALIZATION_PAIRED_TUMOR') + ' *' : $t('FORMS_DIVERSE.ICD_CODE_SIDE_LOCALIZATION') + ' *'"
          :disabled="isDisabled || (Object.keys(multiFormsCount).length > 0 && !!model.seitenlokalisation)"
          :errors="errors.length > 0">
          <div class="layout-row px-4 pb-3 pt-2 flex">
            <div class="layout-column mr-3 layout-align-center-start">

              <!-- radio-element is disabled, when form has status or following forms, value "T" can be set for not-paired tumor, if no status exists -->

              <v-radio-group :dense="denseFields" v-model="model.seitenlokalisation" :error-messages="errors[0]" hide-details
                             class="ma-0 pa-0" style="flex:unset;margin-top: 10px;">
                <v-radio v-for="(value, key) in getSideLocalizationTypes(model.primaertumor_icd_code)"
                         :label="value"
                         :value="key"
                         :key="key"
                         :disabled="isDisabled || (Object.keys(multiFormsCount).length > 0 && model.seitenlokalisation && key !== 'T')"
                         :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                         class="mb-1"
                         :dark="compColor.isInDarkMode">
                </v-radio>
              </v-radio-group>
            </div>

            <!-- Info to side localization of paired tumor -->

            <div v-if="isPairedPrimaryTumor(model.primaertumor_icd_code) && model.seitenlokalisation==='B'"
                 class="flex mx-2 pa-3 report layout-row layout-align-start-center"
                 :class="[!compColor.isInDarkMode ? compColor.prime + ' lighten-5 white--text ' : '']"
                 style="border:1px solid;border-radius: 9px;"
                 :style="{'border-color': compColor.isInDarkMode ? compColor.primeHighlightColor : 'transparent'}">
              <div class="layout-column layout-align-center-center mr-3">
                <v-icon size="30" :color="compColor.prime">mdi-information</v-icon>
              </div>
              <span style="font-size: 14px;" :class="compColor.primeClass">{{ $t('FORMS_DIVERSE.DIAGNOSIS_SIDE_LOCALIZATION_INFO_TEXT') }}</span>
            </div>
          </div>
        </fake-input-frame>

        <span class="error--text" style="line-height:12px;font-size:12px;margin-top: 3px;margin-left:10px;">{{ errors[0] }}</span>
      </validation-provider>
    </div>

    <!-- Primärtumor Diagnosesicherung (nur in Dokumentation)-->

    <div v-if="!isNewDiagnosis" class="flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.diagnosesicherung'"
                           :ref="path + '.diagnosesicherung'"
                           rules="required"
                           class="flex layout-column"
                           v-slot="{ errors }">
        <fake-input-frame :label="$t('FORMS_DIVERSE.ICD_CODE_DIAGNOSTIC_ASSURANCE') + ' *'"
                          :disabled="isDisabled"
                          :errors="errors.length > 0">
          <div class="layout-column px-4 pb-3 pt-2 flex">
            <v-radio-group :dense="denseFields" v-model="model.diagnosesicherung" :error-messages="errors[0]" hide-details
                           class="ma-0 pa-0" style="flex:unset;margin-top: 10px;">
              <v-radio v-for="(type, typesIndex) in DiagnosesicherungTypes"
                       :label="type.text"
                       :value="type.value"
                       :key="typesIndex"
                       :disabled="isDisabled"
                       :title="type.title"
                       :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                       class="mb-1"
                       :dark="compColor.isInDarkMode">
              </v-radio>
            </v-radio-group>
          </div>
        </fake-input-frame>
        <span class="error--text" style="line-height:12px;font-size:12px;margin-top: 3px;margin-left:10px;">{{ errors[0] }}</span>
      </validation-provider>
    </div>
  </div>
</template>

<script>
import metaData from '../shared/formsMetaData'
import utils from '../../../common/utils'
import LxAutocomplete from '../../lx_auto_complete/LxAutocomplete'
import LxTextHighlight from '../../lx_text_highlighter/LxTextHighlight'
import moment from 'moment/moment'

export default {
  name: 'Diagnose',
  mixins: [metaData, utils],
  components: {
    LxAutocomplete,
    LxTextHighlight
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    path: {
      type: String
    },
    subjId: {
      type: String
    },
    isDisabled: {
      type: Boolean
    },
    isNewDiagnosis: {
      type: Boolean,
      default: false
    },
    reportNewDiagnosis: {
      type: Boolean
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    formId: {
      type: String
    },
    patientId: {
      type: String
    },
    classifications: {
      type: Array,
      default: () => ([])
    },
    multiFormsCount: {
      type: Object,
      default: () => ({})
    },
    deathForm: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      model: {},
      resultSearchModel: {},
      resultSearchICDOModel: {},
      DiagnosesicherungTypes: null,
      SeitenlokalisationTypes: null,
      Primaertumor_ICD_VersionTypes: null,
      Primaertumor_ICD_O_VersionTypes: null,
      PairedTumorList: [],
      PairedAdditionalTumors: {},
      NotPairedAdditionalTumors: {},
      DateAccuracyTypes: null,
      error: {},
      setReportValue: false,
      errorDialog: false,
      listBtnGroupReportDiagnosis: [
        { value: true, text: this.$t('FORMS_DIVERSE.RADIO_GROUP_JNU.YES') },
        { value: false, text: this.$t('FORMS_DIVERSE.RADIO_GROUP_JNU.NO') }
      ]
    }
  },
  created () {
    this.DiagnosesicherungTypes = this.formsMetaData.DiagnosesicherungTypes
    this.SeitenlokalisationTypes = this.formsMetaData.SeitenlokalisationTypes
    this.SeitenlokalisationTypesPairedTumors = this.formsMetaData.SeitenlokalisationTypesPairedTumors
    this.Primaertumor_ICD_VersionTypes = this.filterVersions('icd10')
    this.Primaertumor_ICD_O_VersionTypes = this.filterVersions('icdO')
    this.PairedTumorList = this.formsMetaData.PairedTumorList
    this.PairedAdditionalTumors = this.formsMetaData.PairedAdditionalTumors
    this.NotPairedAdditionalTumors = this.formsMetaData.NotPairedAdditionalTumors
    this.DateAccuracyTypes = this.formsMetaData.DateAccuracyLong
  },
  methods: {
    /**
     * Validate date after changing date accuracy
     */
    validateDateAfterChangeOfAccuracy () {
      this.$nextTick(() => {
        if (this.$refs[this.path + '.diagnosedatum']) {
          this.$refs[this.path + '.diagnosedatum'].validate()
        }
      })
    },

    /**
     * Delete ICD10-code, text and localization after changing version
     */
    resetICD10Code () {
      if (this.$refs[this.path + '.primaertumor_icd_version'] && this.$refs[this.path + '.primaertumor_icd_version'].errors?.length === 0) {
        this.$delete(this.model, 'primaertumor_icd_code')
        this.$delete(this.model, 'primaertumor_diagnosetext')
        this.$delete(this.model, 'seitenlokalisation')
      }
    },

    /**
     * Delete ICDO-code and text after changing ICD-O-version
     */
    resetICDOCode () {
      this.$delete(this.model, 'primaertumor_topographie_icd_o')
      this.$delete(this.model, 'primaertumor_topographie_icd_freitext')
    },

    /**
     * Open tab of physician-account to edit cancer register details
     */
    openAccountTab () {
      this.addTabToStore({
        routeName: 'AccountDetails',
        options: {
          headline: 'Account',
          icon: 'mdi-account-circle'
        }
      })
    },

    /**
     * Set date accuracy and version per default after date input
     */
    setDateAccuracyAfterDate () {
      // set diagnosedatum_genauigkeit if not exists after changing diagnosedatum in datepicker
      if (this.model.diagnosedatum) {
        if (!this.model.diagnosedatum_genauigkeit) {
          this.model.diagnosedatum_genauigkeit = 'E'
        }

        // set version in dependency to date, if not exists
        if (moment(this.model.diagnosedatum, ['DD.MM.YYYY', 'DD.MM.YY', moment.ISO_8601]).isValid()) {
          const dateYear = new Date(this.model.diagnosedatum).getFullYear()
          const selectedVersion = this.Primaertumor_ICD_VersionTypes.find((code) => { return code.misdValue === dateYear.toString() })

          // version will only be set, if year is part of version-list, else set it on "Sonstige"
          if (selectedVersion) {
            this.$set(this.model, 'primaertumor_icd_version', selectedVersion.value)
          } else {
            this.$set(this.model, 'primaertumor_icd_version', this.Primaertumor_ICD_VersionTypes[this.Primaertumor_ICD_VersionTypes.length - 1].value)
          }
        }
      }
    },

    /**
     *  Return true is primary tumor is paired tumor
     *
     * @param code The icd-10 code of primary tumor
     * @returns {boolean|*}
     */
    isPairedPrimaryTumor (code) {
      return this.PairedTumorList.indexOf(code) > -1 || this.PairedAdditionalTumors[code]
    },

    /**
     * Return list of side localizations in dependency to existing paired primary tumor
     *
     * @param code The icd-10 code of primary tumor
     */
    getSideLocalizationTypes (code) {
      return this.PairedTumorList.indexOf(code) > -1 || this.PairedAdditionalTumors[code] ? this.SeitenlokalisationTypesPairedTumors : this.SeitenlokalisationTypes
    },

    isBladderDiagnosis (code) {
      const matchDiagnosisRegex = RegExp(/^D41.4|D09.0|C67|C68.9/)
      return matchDiagnosisRegex.test(code)
    },

    /**
     * Get list of icd10 codes via regex in auto-complete
     *
     * @param data THe search string
     */
    getDataForAutocomplete (data) {
      // check, if icd10-code is bladder diagnosis, if true, then change search-string to return only bladder tumors
      const isBladderDiagnosisCode = this.isBladderDiagnosis(this.model.primaertumor_icd_code)

      // restriction for bladder tumors is only active, when other forms exist
      if (this.model.primaertumor_icd_code && isBladderDiagnosisCode && Object.keys(this.multiFormsCount).length > 0) {
        data = 'D41.4|D09.0|C67|C68.9'
      }

      // clear string from special characters to provide errors
      data = this.clearStringForRegexSearch(data)

      let config = {
        opts: {
          limit: 30
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.Primaertumor_ICD_VersionTypes.find(elem => elem.value === this.model.primaertumor_icd_version)

      if (foundVersion?.misdValue) {
        config.query.version = foundVersion.misdValue
      }

      // if selected version is "Sonstige", then use oldest misd-version for request
      if (foundVersion?.value === 'Sonstige') {
        config.query.version = this.Primaertumor_ICD_VersionTypes[this.Primaertumor_ICD_VersionTypes.length - 2].misdValue
      }

      this.$http.get('/icd10?config=' + JSON.stringify(config) + '&filtered=tumor')
        .then(res => {
          this.resultSearchModel = {
            result: res.data.items,
            count: res.data.count,
            version: foundVersion?.value === 'Sonstige' ? 'Sonstige' : res.data.version
          }
        })
        .catch(this.NotifyError)
    },

    /**
     * Set icd10 value via list form auto complete
     *
     * @param data The selected object from list of proposed icd10-codes
     */
    setDiagnosis (data) {
      if (data && data.code) {
        const foundVersion = this.Primaertumor_ICD_VersionTypes.find(elem => elem.misdValue === this.resultSearchModel.version)
        const version = foundVersion?.value ? foundVersion?.value : this.Primaertumor_ICD_VersionTypes[0].value

        this.$set(this.model, 'seitenlokalisation', null)
        this.$set(this.model, 'primaertumor_icd_code', data.code)
        this.$set(this.model, 'primaertumor_diagnosetext', data.description)
        this.$set(this.model, 'primaertumor_icd_version', version)

        if (!this.model.diagnosesicherung) {
          this.$set(this.model, 'diagnosesicherung', '7.1')
        }

        // set side localization of non-paired tumor on "Trifft nicht zu"
        if (!this.isPairedPrimaryTumor(data.code)) {
          this.$set(this.model, 'seitenlokalisation', 'T')
        }

        this.setICDOAfterICD10(data, this.model.primaertumor_topographie_icd_o_version)
      } else {
        if (!this.model.primaertumor_icd_code || typeof data === 'string' || (this.validationRef.errors && this.validationRef.errors[this.path + '.primaertumor_icd_code'] && this.validationRef.errors[this.path + '.primaertumor_icd_code'].length > 0)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of icd10, if not selected from result list
            if (typeof data === 'string') this.$set(this.model, 'primaertumor_icd_code', null)
          })

          if (!this.model.primaertumor_icd_code) {
            this.$set(this.model, 'seitenlokalisation', null)
            this.$set(this.model, 'primaertumor_diagnosetext', undefined)
            this.$set(this.model, 'primaertumor_topographie_icd_o', undefined)
            this.$set(this.model, 'primaertumor_topographie_icd_freitext', undefined)
          }
        }
      }
    },

    /**
     * Get list of icdO codes via regex in auto-complete
     *
     * @param data The search string
     */
    getICDOForAutocomplete (data) {
      if (data && this.model.primaertumor_topographie_icd_o && data !== this.model.primaertumor_topographie_icd_o) {
        this.$set(this.model, 'primaertumor_topographie_icd_freitext', undefined)
      }

      // clear string from special characters to provide errors
      data = this.clearStringForRegexSearch(data)

      let config = {
        opts: {
          limit: 30
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.Primaertumor_ICD_O_VersionTypes.find(elem => elem.value === this.model.primaertumor_topographie_icd_o_version)

      if (foundVersion?.misdValue) {
        config.query.version = foundVersion.misdValue
      }

      this.$http.get('/icdO?config=' + JSON.stringify(config))
        .then(res => {
          this.resultSearchICDOModel = {
            result: res.data.items,
            count: res.data.count || 0,
            version: res.data.version
          }
        })
        .catch(this.NotifyError)
    },

    /**
     * Set icdO value via list form auto complete
     *
     * @param data The selected object from list of proposed icdO-codes
     */
    setICDODiagnosis (data) {
      if (data && data.code) {
        const foundVersion = this.Primaertumor_ICD_O_VersionTypes.find(elem => elem.misdValue === this.resultSearchICDOModel.version)
        const version = foundVersion?.value ? foundVersion?.value : this.formsMetaData.ICDO_Version

        this.$set(this.model, 'primaertumor_topographie_icd_o', data.code)
        this.$set(this.model, 'primaertumor_topographie_icd_freitext', data.description)
        this.$set(this.model, 'primaertumor_topographie_icd_o_version', version)
      } else {
        if (!this.model.primaertumor_topographie_icd_o || typeof data === 'string' || (this.validationRef.errors && this.validationRef.errors[this.path + '.primaertumor_topographie_icd_o'] && this.validationRef.errors[this.path + '.primaertumor_topographie_icd_o'].invalid)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of icdO, if not selected from result list
            if (typeof data === 'string') this.$set(this.model, 'primaertumor_topographie_icd_o', null)
          })

          this.$set(this.model, 'primaertumor_topographie_icd_freitext', undefined)
        }
      }
    }
  },
  computed: {
    userCancerRegister () {
      return this.getUserCancerRegisterDetails().register_id
    },
    userIsPhysician () {
      return this.authStore.getters.userIsPhysician
    },
    deathDate () {
      return this.patientId ? this.$store.getters.diagnosisBaseData(this.patientId).deathDate : this.deathForm?.base?.sterbedatum ? this.deathForm.base.sterbedatum : undefined
    },
    diagnosisCodeIsDisabled () {
      let isBladderDiagnosisCode = false

      if (this.model.primaertumor_icd_code) {
        isBladderDiagnosisCode = this.isBladderDiagnosis(this.model.primaertumor_icd_code)
      }
      // diagnosis of urinary bladder can be changed
      if (isBladderDiagnosisCode) {
        return false
      }

      return (this.classifications.length > 0 && !!this.model.primaertumor_icd_code) || Object.keys(this.multiFormsCount).length > 0
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
        }
      },
      immediate: true
    },
    setReportValue: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue && this.isNewDiagnosis) {
          this.$emit('input-ignore', newValue)
        }
      },
      immediate: true
    },
    reportNewDiagnosis: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue && this.isNewDiagnosis) {
          this.setReportValue = newValue
        }
      },
      immediate: true
    }
  }
}
</script>
